import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const SettingsScreen = () => {
  const navigate = useNavigate();
  const [isPremium, setIsPremium] = useState(false);
  const [trackingMode, setTrackingMode] = useState('');
  const [lastPeriodDate, setLastPeriodDate] = useState('');
  const [cycleLength, setCycleLength] = useState(28);

  useEffect(() => {
    // Load current settings
    setIsPremium(localStorage.getItem('isPremium') === 'true');
    setTrackingMode(localStorage.getItem('trackingMode') || 'cycle');
    setLastPeriodDate(localStorage.getItem('lastPeriodDate') || '');
    setCycleLength(parseInt(localStorage.getItem('cycleLength')) || 28);
  }, []);

  const handleSaveSettings = () => {
    localStorage.setItem('trackingMode', trackingMode);
    if (trackingMode === 'cycle') {
      localStorage.setItem('lastPeriodDate', lastPeriodDate);
      localStorage.setItem('cycleLength', cycleLength.toString());
    }
    
    const destination = isPremium ? '/premium-dashboard' : '/';
    navigate(destination);
  };

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate(isPremium ? '/premium-dashboard' : '/')}
            className="text-sm text-gray-500 mb-4 hover:text-black transition-colors"
          >
            ← Back
          </button>
          
          <h1 className="italiana-font text-2xl text-black mb-2">
            Settings
          </h1>
          
          {isPremium && (
            <span className="text-xs bg-gold text-white px-3 py-1 rounded-full font-medium">
              PREMIUM MEMBER
            </span>
          )}
        </div>

        {/* Settings Form */}
        <div className="space-y-6">
          {/* Tracking Mode */}
          <div>
            <h3 className="text-sm font-medium text-black mb-4">
              Tracking Preference
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => setTrackingMode('cycle')}
                className={`w-full p-4 text-left border rounded-lg transition-all ${
                  trackingMode === 'cycle'
                    ? 'border-gold bg-gold bg-opacity-10'
                    : 'border-gray-300'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                    trackingMode === 'cycle' ? 'border-gold bg-gold' : 'border-gray-300'
                  }`} />
                  <div>
                    <h4 className="font-medium text-black">🩸 Menstrual Cycle + Moon</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Track your period for precise guidance
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setTrackingMode('moon')}
                className={`w-full p-4 text-left border rounded-lg transition-all ${
                  trackingMode === 'moon'
                    ? 'border-gold bg-gold bg-opacity-10'
                    : 'border-gray-300'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                    trackingMode === 'moon' ? 'border-gold bg-gold' : 'border-gray-300'
                  }`} />
                  <div>
                    <h4 className="font-medium text-black">🌙 Moon Phases Only</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Follow lunar cycles for guidance
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Cycle Details (if cycle tracking) */}
          {trackingMode === 'cycle' && (
            <>
              <div>
                <label className="block text-sm font-medium text-black mb-3">
                  Last Period Start Date
                </label>
                <input
                  type="date"
                  value={lastPeriodDate}
                  onChange={(e) => setLastPeriodDate(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-3">
                  Cycle Length
                </label>
                <select
                  value={cycleLength}
                  onChange={(e) => setCycleLength(parseInt(e.target.value))}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                >
                  {[21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35].map(length => (
                    <option key={length} value={length}>
                      {length} days
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Save Button */}
          <motion.button
            onClick={handleSaveSettings}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Save Settings
          </motion.button>

          {/* Premium Status */}
          {isPremium ? (
            <div className="bg-gold bg-opacity-10 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-700">
                ✨ You have full premium access to all features
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-3">
                Want complete guidance for every scenario?
              </p>
              <button
                onClick={() => navigate('/unlock')}
                className="text-sm gold-accent font-medium hover:underline"
              >
                Upgrade to Premium →
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SettingsScreen;